import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import type { StringOption } from '@/api/v1/generated-api.gen'
import { httpService } from '@/api/v1/http.service'
import { arrayOfOptions, requiredString } from '@/utils/zod'

const groupsResponseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    course: z.object({
      id: requiredString(),
      name: requiredString()
    })
  })
)

type ClassGradesFilters = {
  coursesOptions: StringOption[]
  groupsByCourseMap: Record<string, StringOption[]>
}

const parseResponse = (
  coursesResponse: z.infer<typeof arrayOfOptions>,
  groupsResponse: z.infer<typeof groupsResponseSchema>
): ClassGradesFilters => ({
  coursesOptions: coursesResponse,
  groupsByCourseMap: groupsResponse.reduce<Record<string, StringOption[]>>(
    (map, element) => ({
      ...map,
      [element.course.id]: map[element.course.id]
        ? [
            ...map[element.course.id],
            { label: element.name, value: element.id }
          ]
        : [{ label: element.name, value: element.id }]
    }),
    {}
  )
})

type Params = {
  semesterId: string
  classId: string
}

const useClassGradesFilters = (params: Params) =>
  useQuery({
    queryKey: ['panelClassesGroupsOptionsGrades', params],
    staleTime: 60 * 100,
    queryFn: async () => {
      const [coursesFilters, groupsFilters] = await Promise.all([
        httpService.panel.panelClassesGroupsOptionsCoursesList(
          params.classId,
          params.semesterId
        ),
        httpService.panel.panelClassesGroupsList(
          params.classId,
          params.semesterId,
          {
            page_size: 'max',
            fetchKeys: {
              id: true,
              course: true,
              name: true
            }
          }
        )
      ])

      const parsed = parseResponse(
        arrayOfOptions.parse(coursesFilters.results),
        groupsResponseSchema.parse(groupsFilters.results)
      )
      return parsed
    }
  })

export default useClassGradesFilters
